/**
 * Route title mapping for breadcrumb generation
 *
 * Maps route patterns to i18n keys (common:navigation.* or common:breadcrumb.*).
 * Supports both static routes and parameterized routes with :id.
 *
 * Keys are resolved via useTranslation('common') in useBreadcrumb hook.
 */
export const ROUTE_TITLE_KEYS: Record<string, string> = {
  '/': 'navigation.dashboard',
  '/dashboard': 'navigation.dashboard',
  '/vehicles': 'navigation.vehicles',
  '/vehicles/:id': 'breadcrumb.vehicle',
  '/customers': 'navigation.customers',
  '/customers/:id': 'breadcrumb.customer',
  '/inventory': 'navigation.inventory',
  '/sales-invoices': 'navigation.salesInvoices',
  '/sales-invoices/:id': 'breadcrumb.salesInvoice',
  '/purchase-invoices': 'navigation.purchaseInvoices',
  '/purchase-invoices/:id': 'breadcrumb.purchaseInvoice',
  '/purchase-invoices/smart-add': 'breadcrumb.smartAdd',
  '/purchase-invoices/smart-add/:id': 'breadcrumb.smartAdd',
  '/pipeline': 'navigation.pipeline',
  '/pipeline/:id': 'breadcrumb.deal',
  '/calendar': 'navigation.calendar',
  '/garage': 'navigation.garage',
  '/garage/:id': 'breadcrumb.workOrder',
  '/settings': 'navigation.settings',
  '/settings/branding': 'breadcrumb.settingsBranding',
  '/settings/templates': 'breadcrumb.settingsTemplates',
  '/settings/security': 'breadcrumb.settingsSecurity',
  '/settings/preferences': 'breadcrumb.settingsPreferences',
  '/settings/accounting': 'breadcrumb.settingsAccounting',
  '/settings/import': 'breadcrumb.settingsImport',
  '/settings/invoiceTerms': 'breadcrumb.settingsInvoiceTerms',
  '/settings/profile': 'breadcrumb.settingsProfile',
  '/profile': 'breadcrumb.profile',
  '/tasks': 'navigation.tasks',
  '/analytics': 'navigation.analytics',
  '/support': 'navigation.support',
  '/admin/teams': 'navigation.admin',
} as const;

/**
 * @deprecated Use ROUTE_TITLE_KEYS with translation instead
 */
export const ROUTE_TITLES = ROUTE_TITLE_KEYS;

/**
 * Get i18n key for a route path
 * Handles both exact matches and parameterized routes
 *
 * @param path - Route path (e.g., "/vehicles/abc123")
 * @returns i18n key or null if not found
 */
export function getRouteTitleKey(path: string): string | null {
  // Try exact match first
  if (ROUTE_TITLE_KEYS[path]) {
    return ROUTE_TITLE_KEYS[path];
  }

  // Try parameterized match
  const segments = path.split('/').filter(Boolean);
  if (segments.length >= 2) {
    // Replace last segment with :id and try again
    const paramPath = `/${segments.slice(0, -1).join('/')}/:id`;
    if (ROUTE_TITLE_KEYS[paramPath]) {
      return ROUTE_TITLE_KEYS[paramPath];
    }
  }

  return null;
}

/**
 * @deprecated Use getRouteTitleKey with translation instead
 */
export const getRouteTitle = getRouteTitleKey;

/**
 * Check if a route segment looks like an ID
 */
export function isIdSegment(segment: string): boolean {
  // Check for common ID patterns:
  // - UUID-like: abc-123-def
  // - Prefixed: veh_123, cust_456, inv_789
  // - Alphanumeric with length > 8
  return (
    segment.includes('_') ||
    segment.includes('-') ||
    (segment.length > 8 && /^[a-zA-Z0-9]+$/.test(segment))
  );
}
